import { brandDetailsLink } from '../../../hierarchyLinks';
import { useAgencyCampaignContext } from '../../../agencyCampaignContext';
import { AgencyCampaignSection } from '../../../../agency/model';
import * as React from 'react';
import { Link } from '@tanstack/react-router';
import {
  DashboardDataTable,
  DashboardDataTableColumnKind,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';
import { Tooltip, TooltipContent, TooltipTrigger, toast } from '@shigo-ui/ui';

import { formatDisplayDate } from '../../../../../lib/format';
import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { useCampaignBrandOptions } from '../../../useCampaignBrandOptions';
import { useCampaignMutations, useCampaignsQuery } from '../../hooks';
import {
  campaignPageSizes,
  resolveCampaignStatusLabel,
  resolveCampaignStatusTone,
  type Campaign,
} from '../../model';
import { useCampaignsRouteFilters } from '../../useCampaignsRouteFilters';
import { CampaignDialog } from '../CampaignDialog';
import { CampaignActions } from './CampaignActions';
import { CampaignsTableToolbar } from './CampaignsTableToolbar';

export function CampaignsTable({
  parentBrand,
}: { parentBrand?: { id: string; name: string } | undefined } = {}) {
  const scope = useAgencyCampaignContext();
  const { can } = useShigoPermissions();
  const canWrite = Boolean(scope) || can(ShigoPermission.MadBuyCampaignWrite);
  const { setAgencies, filters, queryFilter, setBrand, setPagination, setSearch, setStatuses } =
    useCampaignsRouteFilters();
  const query = useCampaignsQuery({
    ...queryFilter,
    ...(parentBrand ? { brandId: parentBrand.id, agencyIds: [] } : {}),
  });
  const brands = useCampaignBrandOptions(!parentBrand);
  const mutations = useCampaignMutations();
  const [dialogCampaign, setDialogCampaign] = React.useState<Campaign | null | undefined>();
  const [duplicatingCampaignIds, setDuplicatingCampaignIds] = React.useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const columns = React.useMemo(
    () =>
      buildDashboardDataTableColumns<Campaign>([
        {
          id: 'name',
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('campaigns.name'),
          headerClassName: 'w-[36%] pl-4',
          className: 'pl-4 pr-2',
          cell: (campaign) => (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={
                    scope
                      ? '/a/$agencyId/campaigns/$section'
                      : '/campaigns/campaigns/overview/$campaignId'
                  }
                  params={{
                    campaignId: campaign.id,
                    agencyId: scope?.agency.id ?? '',
                    section: AgencyCampaignSection.Campaigns,
                  }}
                  search={scope ? { campaign: campaign.id } : {}}
                  className={dashboardTableTextActionClassName()}
                >
                  {campaign.name}
                </Link>
              </TooltipTrigger>
              <TooltipContent>{campaign.name}</TooltipContent>
            </Tooltip>
          ),
        },
        {
          id: 'brand',
          hidden: Boolean(parentBrand),
          kind: DashboardDataTableColumnKind.Custom,
          header: dashboardT('campaigns.brand'),
          headerClassName: 'w-[24%]',
          className: 'max-w-0',
          cell: (campaign) => {
            const brandName = campaign.brand?.name ?? '-';
            const brandId = campaign.brandId ?? campaign.brand?.id;
            if (!brandId) return brandName;
            return brandName === '-' ? (
              brandName
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    {...brandDetailsLink(brandId, scope?.agency.id)}
                    className={dashboardTableTextActionClassName()}
                  >
                    {brandName}
                  </Link>
                </TooltipTrigger>
                <TooltipContent>{brandName}</TooltipContent>
              </Tooltip>
            );
          },
        },
        {
          id: 'status',
          kind: DashboardDataTableColumnKind.Status,
          header: dashboardT('campaigns.status'),
          label: (campaign) => resolveCampaignStatusLabel(campaign.status),
          tone: (campaign) => resolveCampaignStatusTone(campaign.status),
        },
        {
          id: 'created',
          kind: DashboardDataTableColumnKind.Date,
          header: dashboardT('campaigns.created'),
          headerClassName: 'w-28',
          className: 'w-28 whitespace-nowrap',
          value: (campaign) => formatDisplayDate(campaign.createdOn),
        },
        {
          id: 'actions',
          kind: DashboardDataTableColumnKind.Actions,
          hidden: !canWrite,
          cell: (campaign) => (
            <CampaignActions
              campaign={campaign}
              pending={duplicatingCampaignIds.has(campaign.id)}
              onEdit={() => {
                setDialogCampaign(campaign);
              }}
              onDuplicate={(deepCopyCreatives) => {
                setDuplicatingCampaignIds((current) => new Set(current).add(campaign.id));
                void mutations.duplicate
                  .mutateAsync({ id: campaign.id, deepCopyCreatives })
                  .then(() => {
                    toast.success(dashboardT('campaigns.duplicated'));
                  })
                  .catch(() => undefined)
                  .finally(() => {
                    setDuplicatingCampaignIds((current) => {
                      const next = new Set(current);
                      next.delete(campaign.id);
                      return next;
                    });
                  });
              }}
            />
          ),
        },
      ]),
    [canWrite, duplicatingCampaignIds, mutations.duplicate, parentBrand, scope],
  );
  const Container = parentBrand ? 'div' : 'main';
  return (
    <Container className={parentBrand ? 'min-w-0' : 'page-gutter min-w-0 flex-1 py-4 md:py-6'}>
      <DashboardDataTable
        title={dashboardT('campaigns.title')}
        tableLabel={dashboardT('campaigns.title')}
        columns={columns}
        items={query.data?.content ?? []}
        getRowKey={(campaign) => campaign.id}
        loading={query.isLoading}
        loadingMessage={dashboardT('campaigns.loading')}
        emptyMessage={dashboardT('campaigns.empty')}
        errorMessage={query.isError ? dashboardT('campaigns.loadError') : undefined}
        minWidthClassName="min-w-[840px]"
        toolbar={
          <CampaignsTableToolbar
            showAgencyFilter={!scope}
            agencyIds={filters.agencyIds ?? []}
            onAgenciesChange={setAgencies}
            brandFilter={
              parentBrand
                ? undefined
                : {
                    brandId: filters.brandId,
                    options: brands.data ?? [],
                    loading: brands.isLoading,
                    onChange: setBrand,
                  }
            }
            search={filters.name}
            onSearchChange={setSearch}
            statuses={filters.status}
            onStatusesChange={setStatuses}
            onCreate={
              canWrite
                ? () => {
                    setDialogCampaign(null);
                  }
                : undefined
            }
          />
        }
        pagination={{
          value: {
            page: filters.page,
            pageSize: filters.size,
            totalItems: query.data?.totalElements ?? 0,
          },
          pageSizes: campaignPageSizes,
          disabled: query.isFetching,
          onChange: (next) => {
            setPagination(next.page, next.pageSize);
          },
        }}
      />
      {canWrite && dialogCampaign !== undefined ? (
        <CampaignDialog
          campaign={dialogCampaign}
          parentBrand={parentBrand}
          onClose={() => {
            setDialogCampaign(undefined);
          }}
        />
      ) : null}
    </Container>
  );
}
